/** Очередь выдачи призов: партнёрская сторона статусов из прототипа. */
import type { Claim, ClaimStatus } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import {
  Badge, Button, Card, Countdown, Empty, Field, Icon, Segmented, Sheet, Skeleton, fmtDay, num,
} from '@allswipes/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { useToast } from '../toast';

type Filter = 'pending' | 'ready' | 'done' | 'all';

const FILTERS: Record<Filter, ClaimStatus[] | undefined> = {
  pending: ['unclaimed', 'processing'],
  ready: ['ready'],
  done: ['received', 'reviewed'],
  all: undefined,
};

export function ClaimsScreen() {
  const [filter, setFilter] = useState<Filter>('pending');
  const claims = useResource(() => api.consoleClaims(FILTERS[filter]), [filter]);
  const [active, setActive] = useState<Claim | null>(null);
  const toast = useToast();
  // Акции «приз на упаковке» живут отдельно от этой очереди: там нет заявки,
  // которую оформляют, — человек просто приходит с крышкой. Но приходит он в
  // ту же точку и к тому же кассиру, поэтому вход сюда же.
  const campaigns = useResource(() => api.consoleCampaigns(), []);
  const packs = (campaigns.data?.campaigns ?? []).filter(
    (c) => c.recipe === 'pack' && c.status === 'active',
  );

  return (
    <Layout title="Выдача призов" subtitle="оформленные призы и их статусы">
      {packs.length > 0 && (
        <Card style={{ marginBottom: 18 }}>
          <div className="h2" style={{ marginBottom: 6 }}>Приз на упаковке</div>
          <p className="sub" style={{ marginBottom: 10 }}>
            Человек приходит с крышкой, на которой написан приз. Заявки здесь нет — отметь выдачу
            на экране акции, и счётчик на её странице вырастет.
          </p>
          {packs.map((c) => (
            <div key={c.id} className="row" style={{ gap: 8, marginBottom: 8 }}>
              <span>{c.name}</span>
              <span className="spacer" />
              <Link to={`/campaigns/${c.id}/pack`} className="btn sm">
                Выдать
              </Link>
            </div>
          ))}
        </Card>
      )}

      <div style={{ maxWidth: 520, marginBottom: 18 }}>
        <Segmented
          value={filter}
          options={[
            { value: 'pending', label: 'В работе' },
            { value: 'ready', label: 'Готовы' },
            { value: 'done', label: 'Выданы' },
            { value: 'all', label: 'Все' },
          ]}
          onChange={setFilter}
        />
      </div>

      {claims.loading && <Skeleton count={4} height={56} />}
      {claims.data?.claims.length === 0 && (
        <Empty icon="gift" title="Пусто">
          {filter === 'pending'
            ? 'Нет призов, ждущих обработки.'
            : 'По этому фильтру ничего нет.'}
        </Empty>
      )}

      {claims.data && claims.data.claims.length > 0 && (
        <Card className="flush">
          <table className="tbl" style={{ padding: 15 }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 15 }}>Приз</th>
                <th>Билет / исход</th>
                <th>Статус</th>
                <th>Срок</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {claims.data.claims.map((claim) => (
                <tr key={claim.id}>
                  <td style={{ paddingLeft: 15 }}>
                    <div className="h2">{claim.prize_label}</div>
                    <div className="faint" style={{ fontSize: 11.5 }}>
                      {claim.fulfil === 'instant' ? 'мгновенная выдача' : 'выдача в точке'}
                    </div>
                  </td>
                  <td className="mono" style={{ fontSize: 12.5 }}>
                    {claim.ticket_number || 'мгновенный приз'}
                  </td>
                  <td>
                    <ClaimBadge status={claim.status} />
                    {claim.pickup_point && (
                      <div className="faint" style={{ fontSize: 11.5, marginTop: 3 }}>
                        {claim.pickup_point}
                      </div>
                    )}
                  </td>
                  <td className="faint" style={{ fontSize: 12 }}>
                    {claim.deadline_at ? (
                      claim.status === 'unclaimed' ? (
                        <Countdown to={claim.deadline_at} />
                      ) : (
                        fmtDay(claim.deadline_at)
                      )
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ textAlign: 'right', paddingRight: 15 }}>
                    {claim.status === 'processing' || claim.status === 'unclaimed' ? (
                      <Button size="sm" variant="pri" onClick={() => setActive(claim)}>
                        Приз готов
                      </Button>
                    ) : claim.status === 'reviewed' && claim.rating ? (
                      <span className="row faint" style={{ fontSize: 12, gap: 3, justifyContent: 'flex-end' }}>
                        <Icon name="star" size={12} style={{ fill: 'currentColor' }} />
                        {claim.rating}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {claims.data && claims.data.claims.length > 0 && (
        <p className="sub" style={{ marginTop: 14, fontSize: 12 }}>
          Всего по фильтру: {num(claims.data.claims.length)}. Победитель видит статус в приложении и
          подтверждает получение сам — после этого можно оставить отзыв.
        </p>
      )}

      {active && (
        <ReadySheet
          claim={active}
          onClose={() => setActive(null)}
          onDone={() => {
            toast('Победителю отправлено уведомление');
            setActive(null);
            claims.reload();
          }}
        />
      )}
    </Layout>
  );
}

function ClaimBadge({ status }: { status: ClaimStatus }) {
  switch (status) {
    case 'unclaimed':
      return <Badge tone="warn">не оформлен</Badge>;
    case 'processing':
      return <Badge tone="warn">в обработке</Badge>;
    case 'ready':
      return <Badge tone="prize">готов к выдаче</Badge>;
    case 'received':
      return <Badge tone="good">получен</Badge>;
    case 'reviewed':
      return <Badge tone="good">с отзывом</Badge>;
  }
}

function ReadySheet({
  claim,
  onClose,
  onDone,
}: {
  claim: Claim;
  onClose: () => void;
  onDone: () => void;
}) {
  const [pickup, setPickup] = useState(claim.pickup_point ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.markClaimReady(claim.id, pickup);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title="Приз готов к выдаче" onClose={onClose}>
      <div className="h2">{claim.prize_label}</div>
      {claim.ticket_number && (
        <div className="mono faint" style={{ fontSize: 12, marginTop: 4 }}>
          билет {claim.ticket_number}
        </div>
      )}
      <Field
        label="Где забрать"
        error={error ?? undefined}
        hint="Адрес или название точки — победитель увидит его в приложении."
      >
        <input
          className="input"
          value={pickup}
          onChange={(e) => setPickup(e.target.value)}
          placeholder="Oila Market, Рудаки 55, касса №3"
        />
      </Field>
      <Button variant="pri" block disabled={busy} onClick={() => void submit()}>
        {busy ? 'Сохраняем…' : 'Отметить готовым и уведомить'}
      </Button>
    </Sheet>
  );
}
